import React from 'react';

/**
 * Quick toggle filter chips for common filter scenarios
 * These are fast toggles that can be combined with other filters
 */
const ToggleChipsFilter = ({
  chips = [], // Array of { id, label, icon: Icon, color }
  activeChips = [], // Array of active chip IDs
  onChange,
  className = '',
  size = 'sm', // 'sm' | 'md'
}) => {
  const toggleChip = (chipId) => {
    const newActive = activeChips.includes(chipId)
      ? activeChips.filter(id => id !== chipId)
      : [...activeChips, chipId];
    onChange(newActive);
  };

  const sizeClasses = {
    sm: 'px-2.5 py-1 text-xs gap-1',
    md: 'px-3 py-1.5 text-sm gap-1.5',
  };

  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {chips.map(chip => {
        const isActive = activeChips.includes(chip.id);
        const Icon = chip.icon;
        
        return (
          <button
            key={chip.id}
            onClick={() => toggleChip(chip.id)}
            data-testid={`filter-chip-${chip.id}`}
            className={`
              inline-flex items-center rounded-full font-medium transition-all
              ${sizeClasses[size]}
              ${isActive 
                ? `bg-[var(--clr-brand-primary)] text-white ring-2 ring-[var(--clr-brand-primary)] ring-opacity-30` 
                : `bg-[var(--clr-surface-subtle)] text-[var(--clr-text-secondary)] hover:bg-gray-200`
              }
            `}
            style={isActive && chip.color ? { backgroundColor: chip.color } : undefined}
          >
            {Icon && <Icon className={size === 'sm' ? 'w-3 h-3' : 'w-4 h-4'} />}
            <span>{chip.label}</span>
            {chip.count !== undefined && (
              <span className={`
                ml-1 px-1.5 rounded-full
                ${isActive ? 'bg-white bg-opacity-20' : 'bg-gray-300 bg-opacity-50'}
              `}>
                {chip.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

/**
 * Pre-built Insights Bar with common quick filters
 */
const InsightsBar = ({
  filters = {},
  onFilterChange,
  counts = {}, // { blocked: 0, behind: 0, atRisk: 0, dueThisWeek: 0 }
  currentUser = null,
  className = '',
}) => {
  // Default insight chips
  const defaultChips = [
    { 
      id: 'blocked_only', 
      label: 'Blocked', 
      color: '#8E44AD', 
      count: counts.blocked 
    },
    { 
      id: 'behind_only', 
      label: 'Behind', 
      color: '#E74C3C', 
      count: counts.behind 
    },
    { 
      id: 'at_risk_only', 
      label: 'At Risk', 
      color: '#F39C12', 
      count: counts.atRisk 
    },
    { 
      id: 'due_this_week', 
      label: 'Due This Week', 
      count: counts.dueThisWeek 
    },
  ];

  // Add "My Work" if user is logged in
  if (currentUser) {
    defaultChips.push({
      id: 'my_work_only',
      label: 'My Work',
      count: counts.myWork
    });
  }

  const activeChips = Object.entries(filters)
    .filter(([_, value]) => value === true)
    .map(([key]) => key);

  const handleToggle = (newActive) => {
    const newFilters = {};
    defaultChips.forEach(chip => {
      newFilters[chip.id] = newActive.includes(chip.id);
    });
    onFilterChange(newFilters);
  };

  return (
    <div className={`bg-[var(--clr-surface-card)] border border-gray-200 rounded-lg p-3 ${className}`}>
      <div className="flex items-center gap-3">
        <span className="text-xs font-medium text-[var(--clr-text-muted)] uppercase tracking-wide">
          Quick Filters
        </span>
        <ToggleChipsFilter
          chips={defaultChips}
          activeChips={activeChips}
          onChange={handleToggle}
        />
      </div>
    </div>
  );
};

export { ToggleChipsFilter, InsightsBar };
export default ToggleChipsFilter;
